import { useState, useMemo } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { phoneTest } from "@/utils/phoneValidation";
import { Spinner } from "@/components/ui/spinner";
import { useForm, useWatch } from "react-hook-form";

import { useClinician } from "../api/get-clinician";
import { useProfessions } from "@/api/get-professions";
import { useUpdateClinician } from "../api/update-clinician";
import { ClinicianForm } from "./clinician-form";

export const ClinicianUpdate = () => {
    const { data: clinician, isLoading } = useClinician();

    // Get Professions
    const { data: professions = [] } = useProfessions();

    const personalSchema = yup
        .object({
            firstName: yup.string().required("Please enter your first name"),
            lastName: yup.string().required("Please enter your last name"),
            dateOfBirth: yup
                .string()
                .required("Please enter your date of birth"),
            email: yup
                .string()
                .required("Please enter your email address")
                .email("Enter an email like name@example.com"),
            phoneNumber: yup
                .string()
                .required("Please enter your phone number")
                .test(phoneTest("GB")),
            about: yup
                .string()
                .required("Please enter a description about yourself")
                .max(2500, "Please enter no more than 2500 characters"),
            addressNumber: yup
                .string()
                .required("Please enter your address number/name"),
            addressLine1: yup
                .string()
                .required("Please enter the first line of your address"),
            addressTown: yup
                .string()
                .required("Please enter the town you live in"),
            addressCounty: yup
                .string()
                .required("Please enter the county you live in"),
            addressPostalCode: yup
                .string()
                .required("Please enter the postcode you live in"),
            professionGuid: yup.string().required("Please select a profession"),
            pin: yup
                .string()
                .trim()
                .when("professionGuid", {
                    // Extended clinician professions
                    is: (value) =>
                        professions.find((p) => p.typeGuid === value)
                            ?.isPinOptional === 1,
                    then: (schema) =>
                        // Optional for extended professions, but if entered, must contain a number
                        schema.matches(/^(?=.*\d)[a-zA-Z0-9]*$/, {
                            message: "Must contain at least one number",
                            excludeEmptyString: true,
                        }),
                    otherwise: (schema) =>
                        // Required for all other professions, and must contain a number
                        schema
                            .matches(/^(?=.*\d)[a-zA-Z0-9]*$/, {
                                message: "Must contain at least one number",
                            })
                            .required("Please enter your pin"),
                }),
        })
        .required();

    const methods = useForm({
        resolver: yupResolver(personalSchema),
        mode: "onChange",
        values: clinician,
    });

    // Profile Photo
    const [preview, setPreview] = useState(null);
    const [previewError, setPreviewError] = useState(false);

    const handleProfilePic = () => {
        const input = document.getElementById("profilePic");
        input.click();
    };

    const handleChange = (e) => {
        const file = e.target.files[0];
        if (
            !(
                file.type.startsWith("image/jpeg") ||
                file.type.startsWith("image/png")
            )
        ) {
            setPreview(null);
            setPreviewError(true);
            return;
        }

        const imageUrl = URL.createObjectURL(file);
        setPreview(imageUrl);
        setPreviewError(false);
    };

    // About me
    const aboutTxtCount = useWatch({
        control: methods.control,
        name: "about",
    });

    const aboutTxtLimit = aboutTxtCount?.length || 0;

    // Update 'Statutory regulatory' text
    const selectedProfession = useWatch({
        control: methods.control,
        name: "professionGuid",
    });

    const professionMap = useMemo(() => {
        return Object.fromEntries(professions.map((p) => [p.typeGuid, p]));
    }, [professions]);

    const selectedProfessionTxt =
        professionMap[selectedProfession]?.statutoryBody || "";

    // Submit
    const mutation = useUpdateClinician();

    const onSubmit = (data) => {
        mutation.mutate(data);
    };

    const onResend = (data, override = {}) => {
        mutation.mutate({ ...data, ...override });
    };

    if (isLoading) {
        return (
            <div className="relative">
                <div className="absolute inset-0 z-10 py-10">
                    <div className="grid h-75 place-items-center">
                        <Spinner size="lg" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <ClinicianForm
            methods={methods}
            clinician={clinician}
            onSubmit={onSubmit}
            handleProfilePic={handleProfilePic}
            preview={preview}
            previewError={previewError}
            handleChange={handleChange}
            aboutTxtLimit={aboutTxtLimit}
            professions={professions}
            selectedProfessionTxt={selectedProfessionTxt}
            mutation={mutation}
            onResend={onResend}
        />
    );
};
